import { useLayoutEffect } from 'react';
import {
  getScrollTargetElement,
  SCROLL_RESTORE_TIMEOUT_MS,
  SCROLL_RETRY_DELAYS_MS
} from '../utils/scrollRestoreUtils';

interface UseScrollToEpisodeParams {
  scrollContainerRef: React.RefObject<HTMLDivElement>;
  isLoading: boolean;
  episodesLength: number;
  targetEpisodeId: string | undefined;
  targetCursor: string | null;
  targetCharOffset: string | null;
  onScrollRestoreComplete: () => void;
}

export const useScrollToEpisode = ({
  scrollContainerRef,
  isLoading,
  episodesLength,
  targetEpisodeId,
  targetCursor,
  targetCharOffset,
  onScrollRestoreComplete
}: UseScrollToEpisodeParams): void => {
  useLayoutEffect(() => {
    if (isLoading || episodesLength === 0 || !scrollContainerRef.current || !targetEpisodeId) return;

    const container = scrollContainerRef.current;

    const scrollToTarget = () => {
      const result = getScrollTargetElement(
        container,
        targetEpisodeId,
        targetCursor,
        targetCharOffset
      );
      if (!result) return;

      const { element: target, foundPrecise } = result;
      container.style.scrollBehavior = 'auto';
      target.scrollIntoView({
        inline: 'start',
        block: 'start',
        behavior: 'auto'
      });
      if (foundPrecise) {
        requestAnimationFrame(() =>
          requestAnimationFrame(onScrollRestoreComplete)
        );
      }
    };

    scrollToTarget();
    requestAnimationFrame(() => requestAnimationFrame(scrollToTarget));

    const timeouts = SCROLL_RETRY_DELAYS_MS.map((delay) =>
      setTimeout(scrollToTarget, delay)
    );
    const fallbackTimeout = setTimeout(onScrollRestoreComplete, SCROLL_RESTORE_TIMEOUT_MS);

    return () => {
      timeouts.forEach((id) => clearTimeout(id));
      clearTimeout(fallbackTimeout);
    };
  }, [
    isLoading,
    episodesLength,
    targetEpisodeId,
    targetCursor,
    targetCharOffset,
    onScrollRestoreComplete,
    scrollContainerRef
  ]);
}
